import { createRoot } from 'react-dom/client';

import ActionSuggestionsButton from './Buttons/ActionSuggestionsButton';
import CalendarButton from './Buttons/CalendarButton';
import CalendarButtonSideNav from './Buttons/CalendarButtonSideNav';
import { CalendarCreateEventButton } from './Buttons/CalendarCreateEventButton';
import { CalendarCreateEventLeadsWrapperButton } from './Buttons/CalendarCreateEventLeadsWrapperButton';
import CommissionsButton from './Buttons/CommissionsButton';
import EmailInboxButton from './Buttons/EmailInboxButton';
import MarxButton from './Buttons/MarxButton';
import { FlowBulkAssignment } from './Components/Flows/FlowBulkAssignment';
import { IndFlowHistoryProfile } from './Components/Flows/IndFlowHistoryProfile';
import { PartDPenaltyCalculator } from './Components/PartDPenaltyCalculator';
import { UserContext } from './Context/UserContext';
import ExportModal from './Modals/ExportModal';
import SaveReportModal from './Modals/SaveReportModal';
import AgencyBookPage from './Pages/AgencyBookPage';
import AgentAnalyticsPage from './Pages/AgentAnalyticsPage';
import AgentAutomationsPage from './Pages/AgentAutomationsPage';
import AgentCommissionStatementsPage from './Pages/AgentCommissionStatementsPage';
import AgentEventCalendarPage from './Pages/AgentEventCalendarPage';
import AgentFlowsPage from './Pages/AgentFlowsPage';
import AgentLogsPage from './Pages/AgentLogsPage';
import AgentAnalyticsSchedulePanel from './Panels/AgentAnalyticsSchedulePanel';
import AgentEventCalendarEmbedPanel from './Panels/AgentEventCalendarEmbedPanel';
import { AgentIndProfileCmsFormsPanel } from './Panels/AgentIndProfileCmsFormsPanel';
import AgentSettingsIntegrationPanel from './Panels/AgentSettingsIntegrationPanel';
import AgentSettingsVoIPPhonePanel from './Panels/AgentSettingsVoIPPhonePanel';
import CommissionsPanel from './Panels/CommissionsPanel';
import { ProfileNotesLogsPanel } from './Panels/ProfileNotesLogsPanel';
import TemplateManagerPanel from './Panels/TemplateManagerPanel';
import { UserApiTokensPanel } from './Panels/UserApiTokensPanel';

// Static map to store created roots. Protect against multiple initializations of the same root component
const roots = new Map();

export class ComponentRenderer {
  settings = {};

  constructor(settings = {}) {
    this.settings = settings;
  }

  render(component, path) {
    const element = document.querySelector(path);

    if (element) {
      // If a root already exists, unmount it first so the component is re-rendered on successive calls
      if (roots.has(path)) {
        const existingRoot = roots.get(path);
        existingRoot.unmount();
        roots.delete(path);
      }

      let root = createRoot(element);
      roots.set(path, root);

      // If the user ID and Access Token are available then
      const userData = {
        user: this.settings && this.settings.user ? this.settings.user : null,
        userId: this.settings && this.settings.userId ? parseInt(this.settings.userId) : null,
        userAccessToken:
          this.settings && this.settings.userAccessToken ? this.settings.userAccessToken : null,
      };

      root.render(<UserContext.Provider value={userData}>{component}</UserContext.Provider>);
    }
  }

  syncData() {
    let promises = [];

    if (this.settings.indId && !this.settings.ind) {
      var individualRepository = new MedicareProAPI.IndRepository();
      individualRepository.setAccessToken(this.settings.userAccessToken);

      promises.push(
        individualRepository
          .getInd(this.settings.indId, {
            include: [
              'emails',
              'addresses',
              'spouse',
              'spouse.emails',
              'spouse.addresses',
              'policies',
            ],
            ownerId: this.settings.userId,
          })
          .then((individual) => {
            this.settings.ind = individual;
            return individual;
          })
      );
    }

    if (this.settings.userId && !this.settings.user) {
      var userRepository = new MedicareProAPI.UserRepository();
      userRepository.setAccessToken(this.settings.userAccessToken);

      promises.push(
        userRepository.getUser(this.settings.userId).then((user) => {
          this.settings.user = user;
          return user;
        })
      );
    }

    return Promise.all(promises);
  }

  renderCommissionsPanel(path) {
    this.render(<CommissionsPanel {...this.settings} />, path);
  }

  renderAgentAnalyticsPage(path) {
    this.render(<AgentAnalyticsPage {...this.settings} />, path);
  }

  renderAgentEventCalendarPage(path) {
    this.render(<AgentEventCalendarPage {...this.settings} />, path);
  }

  renderAgencyBookPage(path) {
    this.render(<AgencyBookPage {...this.settings} />, path);
  }

  renderAgentLogsPage(path) {
    this.render(<AgentLogsPage {...this.settings} />, path);
  }

  renderAgentCommissionStatementsPage(path) {
    this.syncData().then((responses) => {
      this.render(<AgentCommissionStatementsPage {...this.settings} />, path);
    });
  }

  renderAgentAutomationsPage(path) {
    this.render(<AgentAutomationsPage {...this.settings} />, path);
  }

  renderAgentSettingsVoIPPanel(path) {
    this.render(<AgentSettingsVoIPPhonePanel {...this.settings} />, path);
  }

  renderAgentSettingsIntegrationPanel(path) {
    this.render(<AgentSettingsIntegrationPanel {...this.settings} />, path);
  }

  renderAgentAnalyticsSchedulePanel(path) {
    this.render(<AgentAnalyticsSchedulePanel {...this.settings} />, path);
  }

  renderProfileNotesLogsPanel(path) {
    this.render(<ProfileNotesLogsPanel {...this.settings} />, path);
  }

  renderEmailInboxButton(path) {
    this.syncData().then((responses) => {
      this.render(<EmailInboxButton {...this.settings} />, path);
    });
  }

  renderCalendarEmbed(path) {
    this.syncData().then((responses) => {
      this.render(<AgentEventCalendarEmbedPanel {...this.settings} />, path);
    });
  }

  renderCalendarButton(path) {
    this.syncData().then((responses) => {
      this.render(<CalendarButton {...this.settings} />, path);
    });
  }

  renderActionSuggestionsButton(path) {
    this.syncData().then((responses) => {
      this.render(<ActionSuggestionsButton {...this.settings} />, path);
    });
  }

  renderCalendarCreateEventButton(path) {
    this.render(<CalendarCreateEventButton {...this.settings} />, path);
  }

  renderCalendarCreateEventLeadsWrapperButton(path) {
    this.render(<CalendarCreateEventLeadsWrapperButton {...this.settings} />, path);
  }

  renderCalendarButtonSideNav(path) {
    this.syncData().then((responses) => {
      this.render(<CalendarButtonSideNav {...this.settings} />, path);
    });
  }

  renderMarxButton(path) {
    this.render(<MarxButton {...this.settings} />, path);
  }

  renderTemplateManagerPanel(path) {
    this.syncData().then((responses) => {
      this.render(<TemplateManagerPanel {...this.settings} />, path);
    });
  }

  renderAgentIndProfileCmsFormsPanel(path) {
    this.render(<AgentIndProfileCmsFormsPanel {...this.settings} />, path);
  }

  renderAgentFlowsPage(path) {
    this.render(<AgentFlowsPage {...this.settings} />, path);
  }

  renderIndFlowHistoryProfile(path, props) {
    const merged = { ...this.settings, ...props };
    this.render(<IndFlowHistoryProfile {...merged} />, path);
  }

  renderFlowBulkAssignment(path, props) {
    const merged = { ...this.settings, ...props };
    this.render(<FlowBulkAssignment {...merged} />, path);
  }

  renderCommissionsButton(path) {
    this.syncData().then((responses) => {
      this.render(<CommissionsButton {...this.settings} />, path);
    });
  }

  renderReportBuilderSaveBtnStart(path, handleSaveReport, filters, includes) {
    this.render(
      <SaveReportModal
        {...this.settings}
        onSaveSuccess={handleSaveReport}
        filters={filters}
        show={true}
        includes={includes}
      />,
      path
    );
  }

  renderExport(path, showExportModal, handleExport, isMailingList, includeData) {
    this.render(
      <ExportModal
        {...this.settings}
        show={showExportModal}
        includeData={includeData}
        onExport={handleExport}
        isMailingList={isMailingList}
      />,
      path
    );
  }

  renderPartDPenaltyCalculator(path) {
    this.render(<PartDPenaltyCalculator {...this.settings} />, path);
  }

  renderUserApiTokensPanel(path, props) {
    const merged = { ...this.settings, ...props };
    this.render(<UserApiTokensPanel {...merged} />, path);
  }

}
